import * as THREE from '../extras/three'
import { ControlPriorities } from '../extras/ControlPriorities'
import { createNode } from '../extras/createNode'
import { System } from './System'
import { storage } from '../storage'
import { buildVisemeTimeline, visemeAt } from '../extras/receptionistVisemes'
import { applyMouthViseme } from '../extras/vrmMouthBindings'
import { localPlayerPosition } from '../extras/localPlayerPosition'
import { findClearPosition } from '../extras/clearPlayerPosition'

const Modes = {
  IDLE: 0,
  TALK: 6,
}

// Keep the camera on the marker's front side; the previous sign framed the avatar from behind.
const CAMERA_OFFSET = new THREE.Vector3(0, 1.55, -0.9)
const LOOK_AT_OFFSET = new THREE.Vector3(0, 1.45, 0)
// XR: the player's feet in front of the desk. The Reception desk reaches 1.44 m in
// front of the marker; 1.9 m keeps the 0.3 m capsule clear of its collider.
const XR_PLAYER_OFFSET = new THREE.Vector3(0, 0, -1.9)
const ZERO = new THREE.Vector3()
const FORWARD = new THREE.Vector3(0, 0, -1)
const v1 = new THREE.Vector3()
const v2 = new THREE.Vector3()

// crypto.randomUUID only exists in secure contexts (HTTPS/localhost); getRandomValues works everywhere.
function createConversationId() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function splitBubbles(text, maxLength = 110) {
  const words = text.trim().split(/\s+/)
  const bubbles = []
  let current = ''
  for (const word of words) {
    if (!current) {
      current = word
    } else if (`${current} ${word}`.length <= maxLength) {
      current += ` ${word}`
    } else {
      bubbles.push(current)
      current = word
    }
  }
  if (current) bubbles.push(current)
  return bubbles
}

export class ClientReceptionist extends System {
  constructor(world) {
    super(world)
    this.root = null
    this.avatar = null
    this.control = null
    this.interactionControl = null
    this.keyActivationPending = false
    this.talking = false
    this.speaking = false
    this.speechSequence = 0
    this.audioContext = null
    this.audioRequest = null
    this.audioSources = new Set()
    this.playbackStart = 0
    this.nextAudioTime = 0
    this.streamComplete = false
    this.visemeTimeline = []
    this.mouthMeshes = []
    this.blinkWait = 2 + Math.random() * 4
    this.blinkTime = -1
    this.nearby = null
    this.bubbles = []
    this.busy = false
    this.conversationId = null
    this.cameraPosition = new THREE.Vector3()
    this.cameraQuaternion = new THREE.Quaternion()
  }

  async start() {
    this.world.on('xrSession', this.onXRSession)
    try {
      const response = await fetch('/api/receptionist')
      if (!response.ok) throw new Error(`configuration request failed (${response.status})`)
      const config = await response.json()
      if (!config.enabled) return

      const source = await this.world.loader.load('avatar', config.model)
      const avatar = source.toNodes().get('avatar')
      if (!avatar) throw new Error('VRM has no avatar node')

      const root = createNode('group', {
        id: 'receptionist',
        position: config.position,
        quaternion: config.quaternion,
      })

      root.add(avatar)
      root.activate({ world: this.world })
      this.root = root
      this.avatar = avatar
      avatar.instance?.raw.scene.traverse(node => {
        if (node.morphTargetDictionary?.Fcl_MTH_A && node.morphTargetInfluences) this.mouthMeshes.push(node)
      })
      this.interactionControl = this.world.controls.bind({ priority: ControlPriorities.ACTION })
      this.interactionControl.keyE.onPress = () => {
        if (!this.nearby || this.talking) return false
        this.keyActivationPending = true
        return true
      }
      this.interactionControl.keyE.onRelease = () => {
        if (!this.keyActivationPending) return
        this.keyActivationPending = false
        if (this.nearby && !this.talking) this.startConversation()
      }
    } catch (err) {
      console.error('failed to load receptionist:', err)
    }
  }

  // A desktop conversation owns the camera; entering or leaving XR ends it.
  onXRSession = () => {
    this.stopConversation(false)
  }

  toggleConversation() {
    if (this.talking) {
      this.stopConversation()
    } else {
      this.startConversation()
    }
  }

  startConversation() {
    if (!this.root || !this.avatar || this.talking) return
    if (!this.audioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass()
        this.audioContext.resume().catch(() => {})
      }
    }
    this.talking = true
    this.conversationId = createConversationId()
    this.busy = false
    const playerName = this.world.entities.player?.data.name
    const visitorName = typeof playerName === 'string' ? playerName.trim().slice(0, 40) : ''
    const italian = navigator.language?.toLowerCase().startsWith('it')
    const welcome = italian
      ? `Ciao${visitorName ? ` ${visitorName}` : ''}, sono l'assistente Saratoga.`
      : `Hello${visitorName ? ` ${visitorName}` : ''}, I'm Saratoga's assistant.`
    this.bubbles = splitBubbles(welcome).map(text => ({ role: 'receptionist', text }))
    this.avatar.setLocomotion(Modes.IDLE, ZERO, FORWARD)
    this.world.controls.releaseAllButtons()
    if (this.world.xr.session) {
      // XR: the HTML chat is shown in the HUD. The headset keeps tracking the head;
      // the player is placed in front of the receptionist and held still.
      const player = this.world.entities.player
      if (player?.placeXR) {
        const position = XR_PLAYER_OFFSET.clone().applyQuaternion(this.root.quaternion).add(this.root.position)
        const target = LOOK_AT_OFFSET.clone().applyQuaternion(this.root.quaternion).add(this.root.position)
        player.placeXR(position, target)
        this.releaseLocomotion = player.lockLocomotion()
      }
      this.broadcast()
      this.speak(welcome)
      return
    }
    this.world.controls.unlockPointer()
    this.broadcast()

    this.control = this.world.controls.bind({ priority: ControlPriorities.APP })
    this.control.camera.write = true
    this.control.camera.position.setFromMatrixPosition(this.world.camera.matrixWorld)
    this.control.camera.quaternion.setFromRotationMatrix(this.world.camera.matrixWorld)
    this.control.camera.zoom = 0
    this.control.hideReticle()

    for (const prop of ['keyW', 'keyA', 'keyS', 'keyD', 'space']) {
      this.control[prop].capture = true
    }
    this.control.escape.capture = true
    this.control.escape.onPress = () => {
      this.stopConversation()
      return true
    }

    this.updateCameraTarget()
    this.speak(welcome)
  }

  stopSpeech() {
    this.speechSequence++
    this.speaking = false
    this.audioRequest?.abort()
    this.audioRequest = null
    for (const source of this.audioSources) source.stop()
    this.audioSources.clear()
    this.playbackStart = 0
    this.nextAudioTime = 0
    this.streamComplete = false
    this.visemeTimeline = []
    this.setViseme(null)
  }

  setViseme(viseme) {
    if (this.avatar?.instance?.raw.mouthBindings?.length) {
      applyMouthViseme(this.avatar.instance.raw.mouthBindings, viseme, 0.4)
      return
    }
    for (const mesh of this.mouthMeshes) {
      for (const vowel of 'AIUEO') {
        const index = mesh.morphTargetDictionary[`Fcl_MTH_${vowel}`]
        if (index !== undefined) mesh.morphTargetInfluences[index] = viseme === vowel.toLowerCase() ? 0.4 : 0
      }
    }
  }

  async speak(text) {
    this.stopSpeech()
    if (!this.talking || !this.audioContext) return
    const sequence = this.speechSequence
    const controller = new AbortController()
    this.audioRequest = controller
    try {
      await this.audioContext.resume()
      const response = await fetch(`${this.world.network.apiUrl}/receptionist/speech`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${storage.get('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      })
      if (!response.ok || !response.body) return
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let pending = ''
      let finished = false
      const consume = line => {
        if (!line) return
        const event = JSON.parse(line)
        if (event.audio) {
          this.queueAudio(event.audio, text, sequence)
        } else if (event.done) {
          finished = true
          this.streamComplete = true
          if (this.playbackStart) this.visemeTimeline = buildVisemeTimeline(text, this.nextAudioTime - this.playbackStart)
          if (!this.audioSources.size && sequence === this.speechSequence) this.stopSpeech()
        } else if (event.error) {
          finished = true
          this.stopSpeech()
        }
      }
      while (sequence === this.speechSequence) {
        const { done, value } = await reader.read()
        if (done) break
        pending += decoder.decode(value, { stream: true })
        let boundary
        while ((boundary = pending.indexOf('\n')) !== -1) {
          consume(pending.slice(0, boundary))
          pending = pending.slice(boundary + 1)
        }
      }
      if (!finished && sequence === this.speechSequence) this.stopSpeech()
    } catch {
      if (sequence === this.speechSequence) this.stopSpeech()
    }
  }

  queueAudio(base64, text, sequence) {
    if (sequence !== this.speechSequence || !this.audioContext) return
    const binary = atob(base64)
    if (!binary.length || binary.length % 2) throw new Error('Invalid PCM audio')
    const sampleCount = binary.length / 2
    const audioBuffer = this.audioContext.createBuffer(1, sampleCount, 24000)
    const samples = audioBuffer.getChannelData(0)
    for (let i = 0; i < sampleCount; i++) {
      const sample = binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8)
      samples[i] = (sample > 32767 ? sample - 65536 : sample) / 32768
    }
    const source = this.audioContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(this.audioContext.destination)
    const start = Math.max(this.audioContext.currentTime + 0.06, this.nextAudioTime)
    if (!this.playbackStart) {
      this.playbackStart = start
      this.visemeTimeline = buildVisemeTimeline(text, Math.max(0.5, text.length / 13))
    }
    this.nextAudioTime = start + audioBuffer.duration
    this.audioSources.add(source)
    source.onended = () => {
      this.audioSources.delete(source)
      if (sequence !== this.speechSequence) return
      if (this.streamComplete && !this.audioSources.size) this.stopSpeech()
      else if (!this.audioSources.size) this.speaking = false
    }
    source.start(start)
    this.speaking = true
  }

  updateCameraTarget() {
    if (!this.root) return
    this.cameraPosition.copy(CAMERA_OFFSET).applyQuaternion(this.root.quaternion).add(this.root.position)
    const lookAt = LOOK_AT_OFFSET.clone().applyQuaternion(this.root.quaternion).add(this.root.position)
    const matrix = new THREE.Matrix4().lookAt(this.cameraPosition, lookAt, new THREE.Vector3(0, 1, 0))
    this.cameraQuaternion.setFromRotationMatrix(matrix)
  }

  update(delta) {
    const blinkBindings = this.avatar?.instance?.raw.blinkBindings
    if (blinkBindings?.length) {
      if (this.blinkTime < 0) {
        this.blinkWait -= delta
        if (this.blinkWait <= 0) this.blinkTime = 0
      }
      if (this.blinkTime >= 0) {
        this.blinkTime += delta
        // Fast closure (60 ms), slower reopening (120 ms).
        const phase = this.blinkTime < 0.06 ? this.blinkTime / 0.06 : 1 - (this.blinkTime - 0.06) / 0.12
        const value = Math.max(0, Math.min(1, phase))
        applyMouthViseme(blinkBindings, 'blink', value * value * (3 - 2 * value))
        if (this.blinkTime >= 0.18) {
          this.blinkTime = -1
          this.blinkWait = 2 + Math.random() * 4
        }
      }
    }
    if (this.root) {
      const nearby = localPlayerPosition(this.world).distanceTo(this.root.position) <= 3
      if (nearby !== this.nearby) {
        this.nearby = nearby
        this.broadcast()
      }
    }
    this.updateXRButton()
    if (!this.talking) return
    const speechTime = this.audioContext?.currentTime - this.playbackStart
    this.setViseme(this.speaking && speechTime >= 0 ? visemeAt(this.visemeTimeline, speechTime) : null)
    const headMatrix = this.avatar.getBoneTransform('head')
    if (headMatrix) {
      v1.setFromMatrixPosition(headMatrix)
      // In XR the receptionist looks at the headset.
      const target = this.control ? this.cameraPosition : v2.setFromMatrixPosition(this.world.camera.matrixWorld)
      v2.subVectors(target, v1).normalize()
      this.avatar.setLocomotion(this.speaking ? Modes.TALK : Modes.IDLE, ZERO, v2)
    }
    if (!this.control) return
    const alpha = 1 - Math.exp(-6 * delta)
    this.control.camera.position.lerp(this.cameraPosition, alpha)
    this.control.camera.quaternion.slerp(this.cameraQuaternion, alpha)
  }

  updateXRButton() {
    const visible = this.root && this.world.xr.session && this.nearby && !this.talking && !this.world.courseRoom?.state.active && !this.world.courseRoom?.graphOpen && !this.world.ui.state.pane
    if (!visible) {
      if (this.xrButton?.mounted) {
        this.world.pointer?.cancelForNode(this.xrButton)
        this.xrButton.deactivate()
      }
      return
    }
    if (!this.xrButton) {
      const position = new THREE.Vector3().copy(this.root.position)
      const quaternion = this.root.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI))
      this.xrButton = createNode('ui', { width: 440, height: 64, size: 0.002, position: position.toArray(), quaternion: quaternion.toArray(), transparent: false, backgroundColor: '#24364b', borderRadius: 0, justifyContent: 'center' })
      this.xrButton.add(createNode('uitext', { value: 'Talk with receptionist', fontSize: 23, color: '#ffffff', textAlign: 'center', padding: 12, cursor: 'pointer', onPointerDown: e => e.stopPropagation(), onPointerUp: () => {
        if (this.root && this.world.xr.session && localPlayerPosition(this.world).distanceTo(this.root.position) <= 3 && !this.talking) this.startConversation()
      } }))
    }
    const head = this.avatar?.getBoneTransform('head')
    if (head) this.xrButton.position.setFromMatrixPosition(head)
    else this.xrButton.position.copy(this.root.position).add(new THREE.Vector3(0, 1.65, 0))
    // Head bone is inside the skull: leave a small gap above the crown.
    this.xrButton.position.y += 0.28
    if (!this.xrButton.mounted) {
      this.xrButton.activate({ world: this.world })
      // This opaque sign must also occlude LEDs drawn behind it.
      this.xrButton.material.depthWrite = true
    }
  }

  stopConversation(restoreControl = true) {
    if (!this.talking) return
    this.talking = false
    this.stopSpeech()
    this.audioContext?.close()
    this.audioContext = null
    this.conversationId = null
    this.busy = false
    this.avatar?.setLocomotion(Modes.IDLE, ZERO, FORWARD)
    this.control?.release()
    this.control = null
    if (this.releaseLocomotion) {
      this.releaseLocomotion()
      this.releaseLocomotion = null
      this.leaveDesk()
    }
    this.world.controls.releaseAllButtons()
    this.broadcast()
    if (restoreControl && !this.world.xr.session) this.world.controls.lockPointer()
  }

  // After an XR conversation, never leave the player touching the desk or other colliders.
  leaveDesk() {
    const player = this.world.entities.player
    if (!player?.teleport || !this.root) return
    const forward = v1.set(0, 0, -1).applyQuaternion(this.root.quaternion)
    const position = findClearPosition(this.world.physics, this.root.position, forward)
    if (position) player.teleport({ position })
  }

  async sendMessage(text) {
    const value = typeof text === 'string' ? text.trim() : ''
    if (!this.talking || !value || this.busy || value.length > 500) return false
    const conversationId = this.conversationId
    this.busy = true
    this.stopSpeech()
    this.bubbles = [...this.bubbles, { role: 'user', text: value }]
    this.broadcast()
    try {
      const response = await fetch(`${this.world.network.apiUrl}/receptionist/message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${storage.get('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: value, conversationId }),
      })
      const result = await response.json()
      if (this.talking && this.conversationId === conversationId) {
        const answer = response.ok ? result.answer : (result.error || 'The receptionist could not reply.')
        this.addResponse(answer)
        if (response.ok) this.speak(answer)
      }
    } catch {
      if (this.talking && this.conversationId === conversationId) {
        this.addResponse('The receptionist could not reply. Please try again.')
      }
    } finally {
      if (this.conversationId === conversationId) {
        this.busy = false
        this.broadcast()
      }
    }
    return true
  }

  addResponse(text) {
    this.bubbles = [...this.bubbles, ...splitBubbles(text).map(part => ({ role: 'receptionist', text: part }))]
    this.broadcast()
  }

  broadcast() {
    this.world.emit('receptionist-chat', {
      active: this.talking,
      nearby: !!this.nearby,
      bubbles: this.bubbles,
      busy: this.busy,
    })
  }

  destroy() {
    this.world.off('xrSession', this.onXRSession)
    // The world is shutting down: release without moving the player.
    this.releaseLocomotion?.()
    this.releaseLocomotion = null
    this.stopConversation(false)
    this.keyActivationPending = false
    this.interactionControl?.release()
    this.interactionControl = null
    this.world.pointer?.cancelForNode(this.xrButton)
    this.xrButton?.deactivate()
    this.xrButton = null
    this.root?.deactivate()
    this.root = null
    this.avatar = null
    this.mouthMeshes = []
  }
}
